import { useTranslation } from "react-i18next";
import { OPT_DIM, SEL_MIN_BAR } from "../styles";
import type { HeadsetOption } from "../types";
import { Select } from "./Select";

type Props = {
  options: HeadsetOption[];
  selectedHeadsetId: string | null;
  onSelect: (id: string) => void;
  onListShow: () => void;
  onListHide: () => void;
};

/**
 * Works in every state, online or not: ids are never dropped from the list, so a
 * headset you are about to put on stays listed and selectable, marked with a hollow
 * dot. The list opens upward — the dock sits on the bottom edge of the viewport, so
 * PrimeReact flips it there on its own.
 */
export function HeadsetPicker({
  options,
  selectedHeadsetId,
  onSelect,
  onListShow,
  onListHide,
}: Props) {
  const { t } = useTranslation();
  const empty = t("HeadsetDock.NoHeartbeatsYet");

  return (
    <Select
      value={selectedHeadsetId}
      options={options}
      optionLabel="id"
      optionValue="id"
      dataKey="id"
      minWidthClass={SEL_MIN_BAR}
      onChange={(e) => onSelect(e.value)}
      onShow={onListShow}
      onHide={onListHide}
      emptyMessage={empty}
      // nothing selected yet reads as the empty state rather than as a blank pill
      placeholder={empty}
      valueTemplate={(option?: HeadsetOption) =>
        option ? `${option.online ? "●" : "○"}  ${option.id}` : empty
      }
      itemTemplate={({ id, online }: HeadsetOption) => (
        // The row itself carries OPT_SELECTED, so an offline row only dims while it
        // is not the selected one — otherwise this inner colour would win over it.
        <span className={!online && id !== selectedHeadsetId ? OPT_DIM : undefined}>
          {online ? "●" : "○"}
          &nbsp;&nbsp;
          {id}
        </span>
      )}
    />
  );
}
